import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import AptIcon from 'assets/salesNew/apt.svg';
import Input from 'common-ui/input';
import {
  getAdministrativeDistricts,
  getAdministrativeRegions,
  getBuildings,
  getCities,
  getCountries,
  getStreets,
} from 'features/newsales/newsalesActions';

import { useLeadForm } from '../LeadFormContext';
import {
  AptImg,
  Container,
  ErrorMessage,
  InputGroup,
  InputWrapper,
  Label,
  MainInfoContent,
  MainInfoTab,
  SuggestionItem,
  SuggestionList,
  TabBlock,
  TabBlockSubTitle,
  TabBlockTitle,
  TabContent,
  TabContentTitle,
  Title,
} from './MainInfo.styles';

const MainInfo = ({ selectedType, setSelectedType, onValidationChange, shouldValidate }) => {
  const dispatch = useDispatch();
  const { formData, updateFormData } = useLeadForm();

  const [countryInput, setCountryInput] = useState(formData.countryId ? '...' : '');
  const [regionInput, setRegionInput] = useState(formData.districtId ? '...' : '');
  const [cityInput, setCityInput] = useState(formData.cityId ? '...' : '');
  const [districtInput, setDistrictInput] = useState(
    formData.administrativeRegionId ? '...' : ''
  );
  const [streetInput, setStreetInput] = useState(formData.streetId ? '...' : '');
  const [buildingInput, setBuildingInput] = useState(formData.buildingHouseId ? '...' : '');
  const [apt, setApt] = useState(formData.apt || '');
  const [entrance, setEntrance] = useState(formData.entrance || '');

  const [errors, setErrors] = useState({
    country: '',
    region: '',
    city: '',
    street: '',
    building: '',
  });
  const [touched, setTouched] = useState({
    country: false,
    region: false,
    city: false,
    street: false,
    building: false,
  });

  const [selectedCountryId, setSelectedCountryId] = useState(formData.countryId);
  const [selectedRegionId, setSelectedRegionId] = useState(formData.districtId);
  const [selectedCityId, setSelectedCityId] = useState(formData.cityId);
  const [selectedDistrictId, setSelectedDistrictId] = useState(
    formData.administrativeRegionId
  );
  const [selectedStreetId, setSelectedStreetId] = useState(formData.streetId);
  const [selectedBuildingId, setSelectedBuildingId] = useState(formData.buildingHouseId);

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [streets, setStreets] = useState([]);
  const [buildings, setBuildings] = useState([]);

  const [countrySuggestions, setCountrySuggestions] = useState([]);
  const [regionSuggestions, setRegionSuggestions] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [buildingSuggestions, setBuildingSuggestions] = useState([]);

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingStreets, setLoadingStreets] = useState(false);
  const [loadingBuildings, setLoadingBuildings] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const result = await dispatch(getCountries({ limit: 1000 }));
        if (result.payload?.data) {
          setCountries(result.payload?.data || []);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, [dispatch]);

  useEffect(() => {
    if (!selectedCountryId) {
      setRegions([]);
      setRegionInput('');
      setCityInput('');
      setDistrictInput('');
      setStreetInput('');
      setBuildingInput('');
      setSelectedRegionId(null);
      setSelectedCityId(null);
      setSelectedDistrictId(null);
      setSelectedStreetId(null);
      return;
    }

    const fetchRegions = async () => {
      setLoadingRegions(true);
      try {
        const result = await dispatch(
          getAdministrativeRegions({ countryId: selectedCountryId, limit: 1000 })
        );
        if (result.payload?.data) {
          setRegions(result.payload?.data || []);
        }
      } catch (error) {
        console.error('Error fetching regions:', error);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, [selectedCountryId, dispatch]);

  useEffect(() => {
    if (!selectedCountryId || !selectedRegionId) {
      setCities([]);
      setCityInput('');
      setDistrictInput('');
      setStreetInput('');
      setBuildingInput('');
      setSelectedCityId(null);
      setSelectedDistrictId(null);
      setSelectedStreetId(null);
      return;
    }

    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const result = await dispatch(
          getCities({
            countryId: selectedCountryId,
            districtId: selectedRegionId,
            limit: 1000,
          })
        );
        if (result.payload?.data) {
          setCities(result.payload?.data || []);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [selectedCountryId, selectedRegionId, dispatch]);

  useEffect(() => {
    if (!selectedCountryId || !selectedRegionId || !selectedCityId) {
      setStreets([]);
      setDistricts([]);
      setDistrictInput('');
      setStreetInput('');
      setBuildingInput('');
      setSelectedDistrictId(null);
      setSelectedStreetId(null);
      return;
    }

    const fetchStreetsAndDistricts = async () => {
      setLoadingStreets(true);
      setLoadingDistricts(true);
      try {
        const [streetsResult, districtsResult] = await Promise.all([
          dispatch(
            getStreets({
              countryId: selectedCountryId,
              districtId: selectedRegionId,
              cityId: selectedCityId,
              limit: 1000,
            })
          ),
          dispatch(
            getAdministrativeDistricts({
              countryId: selectedCountryId,
              districtId: selectedRegionId,
              cityId: selectedCityId,
              limit: 1000,
            })
          ),
        ]);

        if (streetsResult.payload?.data) {
          setStreets(streetsResult.payload?.data || []);
        }
        if (districtsResult.payload?.data) {
          setDistricts(districtsResult.payload?.data || []);
        }
      } catch (error) {
        console.error('Error fetching streets/districts:', error);
      } finally {
        setLoadingStreets(false);
        setLoadingDistricts(false);
      }
    };
    fetchStreetsAndDistricts();
  }, [selectedCountryId, selectedRegionId, selectedCityId, dispatch]);

  useEffect(() => {
    if (!selectedCountryId || !selectedRegionId || !selectedCityId || !selectedStreetId) {
      setBuildings([]);
      setBuildingInput('');
      return;
    }

    const fetchBuildings = async () => {
      setLoadingBuildings(true);
      try {
        const params = {
          countryId: selectedCountryId,
          districtId: selectedRegionId,
          cityId: selectedCityId,
          streetId: selectedStreetId,
          limit: 1000,
        };

        if (selectedDistrictId) {
          params.administrativeDistrictId = selectedDistrictId;
        }

        const result = await dispatch(getBuildings(params));
        if (result.payload?.data) {
          setBuildings(result.payload?.data || []);
        }
      } catch (error) {
        console.error('Error fetching buildings:', error);
      } finally {
        setLoadingBuildings(false);
      }
    };
    fetchBuildings();
  }, [
    selectedCountryId,
    selectedRegionId,
    selectedCityId,
    selectedStreetId,
    selectedDistrictId,
    dispatch,
  ]);

  useEffect(() => {
    if (countries.length > 0 && selectedCountryId && countryInput === '...') {
      const country = countries.find((c) => c.id === selectedCountryId);
      if (country) setCountryInput(country.name);
    }
  }, [countries, selectedCountryId, countryInput]);

  useEffect(() => {
    if (regions.length > 0 && selectedRegionId && regionInput === '...') {
      const region = regions.find((r) => r.id === selectedRegionId);
      if (region) setRegionInput(region.name);
    }
  }, [regions, selectedRegionId, regionInput]);

  useEffect(() => {
    if (cities.length > 0 && selectedCityId && cityInput === '...') {
      const city = cities.find((c) => c.id === selectedCityId);
      if (city) setCityInput(city.name);
    }
  }, [cities, selectedCityId, cityInput]);

  useEffect(() => {
    if (districts.length > 0 && selectedDistrictId && districtInput === '...') {
      const district = districts.find((d) => d.id === selectedDistrictId);
      if (district) setDistrictInput(district.name);
    }
  }, [districts, selectedDistrictId, districtInput]);

  useEffect(() => {
    if (streets.length > 0 && selectedStreetId && streetInput === '...') {
      const street = streets.find((s) => s.id === selectedStreetId);
      if (street) setStreetInput(street.name);
    }
  }, [streets, selectedStreetId, streetInput]);

  useEffect(() => {
    if (buildings.length > 0 && selectedBuildingId && buildingInput === '...') {
      const building = buildings.find((b) => b.id === selectedBuildingId);
      if (building) setBuildingInput(building.name);
    }
  }, [buildings, selectedBuildingId, buildingInput]);

  const handleCountryChange = (value) => {
    setCountryInput(value);
    if (value.trim()) {
      const filtered = countries.filter((country) =>
        country.name.toLowerCase().includes(value.toLowerCase())
      );
      setCountrySuggestions(filtered);
    } else {
      setCountrySuggestions([]);
    }
  };

  const handleRegionChange = (value) => {
    setRegionInput(value);
    if (value.trim() && selectedCountryId) {
      const filtered = regions.filter((region) =>
        region.name.toLowerCase().includes(value.toLowerCase())
      );
      setRegionSuggestions(filtered);
    } else {
      setRegionSuggestions([]);
    }
  };

  const handleCityChange = (value) => {
    setCityInput(value);
    if (value.trim() && selectedRegionId) {
      const filtered = cities.filter((city) =>
        city.name.toLowerCase().includes(value.toLowerCase())
      );
      setCitySuggestions(filtered);
    } else {
      setCitySuggestions([]);
    }
  };

  const handleDistrictChange = (value) => {
    setDistrictInput(value);
    if (value.trim() && selectedCityId) {
      const filtered = districts.filter((district) =>
        district.name.toLowerCase().includes(value.toLowerCase())
      );
      setDistrictSuggestions(filtered);
    } else {
      setDistrictSuggestions([]);
    }
  };

  const handleStreetChange = (value) => {
    setStreetInput(value);
    if (value.trim() && selectedCityId) {
      const filtered = streets.filter((street) =>
        street.name.toLowerCase().includes(value.toLowerCase())
      );
      setStreetSuggestions(filtered);
    } else {
      setStreetSuggestions([]);
    }
  };

  const handleBuildingChange = (value) => {
    setBuildingInput(value);
    if (value.trim() && selectedStreetId) {
      const filtered = buildings.filter((building) =>
        building.name.toLowerCase().includes(value.toLowerCase())
      );
      setBuildingSuggestions(filtered);
    } else {
      setBuildingSuggestions([]);
    }
  };

  const selectCountry = (country) => {
    setCountryInput(country.name);
    setSelectedCountryId(country.id);
    setCountrySuggestions([]);
  };

  const selectRegion = (region) => {
    setRegionInput(region.name);
    setSelectedRegionId(region.id);
    setRegionSuggestions([]);
  };

  const selectCity = (city) => {
    setCityInput(city.name);
    setSelectedCityId(city.id);
    setCitySuggestions([]);
  };

  const selectDistrict = (district) => {
    setDistrictInput(district.name);
    setSelectedDistrictId(district.id);
    setDistrictSuggestions([]);
  };

  const selectStreet = (street) => {
    setStreetInput(street.name);
    setSelectedStreetId(street.id);
    setStreetSuggestions([]);
  };

  const selectBuilding = (building) => {
    setBuildingInput(building.name);
    setSelectedBuildingId(building.id);
    setBuildingSuggestions([]);
  };

  useEffect(() => {
    const updates = {
      countryId: selectedCountryId,
      districtId: selectedRegionId,
      cityId: selectedCityId,
      administrativeRegionId: selectedDistrictId,
      streetId: selectedStreetId,
      buildingHouseId: selectedBuildingId,
      apt: apt || null,
      entrance: entrance || null,
    };

    updateFormData(updates);
  }, [
    selectedCountryId,
    selectedRegionId,
    selectedCityId,
    selectedDistrictId,
    selectedStreetId,
    selectedBuildingId,
    apt,
    entrance,
  ]);

  useEffect(() => {
    updateFormData({ customerType: selectedType });
  }, [selectedType]);

  const validateForm = () => {
    const newErrors = {
      country: '',
      region: '',
      city: '',
      street: '',
      building: '',
    };

    if (!selectedCountryId) {
      newErrors.country = 'Country is required';
    }
    if (!selectedRegionId) {
      newErrors.region = 'Region is required';
    }
    if (!selectedCityId) {
      newErrors.city = 'City is required';
    }
    if (!selectedStreetId) {
      newErrors.street = 'Street is required';
    }
    if (!selectedBuildingId) {
      newErrors.building = 'Building/House is required';
    }

    setErrors(newErrors);

    const isValid =
      selectedCountryId &&
      selectedRegionId &&
      selectedCityId &&
      selectedStreetId &&
      selectedBuildingId;

    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm();
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [
    selectedCountryId,
    selectedRegionId,
    selectedCityId,
    selectedStreetId,
    selectedBuildingId,
  ]);

  useEffect(() => {
    if (shouldValidate) {
      setTouched({
        country: true,
        region: true,
        city: true,
        street: true,
        building: true,
      });
    }
  }, [shouldValidate]);

  return (
    <Container>
      <Title>Add Lead</Title>
      <MainInfoContent>
        <MainInfoTab>
          <TabBlock isActive={selectedType === 'B2C'} onClick={() => setSelectedType('B2C')}>
            <TabBlockTitle isActive={selectedType === 'B2C'}>B2C</TabBlockTitle>
            <TabBlockSubTitle isActive={selectedType === 'B2C'}>
              Individual Customer
            </TabBlockSubTitle>
          </TabBlock>

          <TabBlock isActive={selectedType === 'B2B'} onClick={() => setSelectedType('B2B')}>
            <TabBlockTitle isActive={selectedType === 'B2B'}>B2B</TabBlockTitle>
            <TabBlockSubTitle isActive={selectedType === 'B2B'}>
              Business Client
            </TabBlockSubTitle>
          </TabBlock>
        </MainInfoTab>

        <TabContent>
          <TabContentTitle>Address</TabContentTitle>

          <InputGroup>
            <InputWrapper hasError={touched.country && errors.country}>
              <Label>
                Country <span>*</span>
              </Label>
              <Input
                type="text"
                placeholder={loadingCountries ? 'Loading...' : 'Type to search...'}
                value={countryInput}
                onChange={(e) => handleCountryChange(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, country: true }))}
                disabled={loadingCountries}
              />
              {countrySuggestions.length > 0 && (
                <SuggestionList>
                  {countrySuggestions.map((country) => (
                    <SuggestionItem
                      key={country.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectCountry(country);
                      }}
                    >
                      {country.name}
                    </SuggestionItem>
                  ))}
                </SuggestionList>
              )}
              {touched.country && errors.country && (
                <ErrorMessage>{errors.country}</ErrorMessage>
              )}
            </InputWrapper>

            <InputWrapper hasError={touched.region && errors.region}>
              <Label>
                Region <span>*</span>
              </Label>
              <Input
                type="text"
                placeholder={loadingRegions ? 'Loading...' : 'Type to search...'}
                value={regionInput}
                onChange={(e) => handleRegionChange(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, region: true }))}
                disabled={!selectedCountryId || loadingRegions}
              />
              {regionSuggestions.length > 0 && (
                <SuggestionList>
                  {regionSuggestions.map((region) => (
                    <SuggestionItem
                      key={region.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectRegion(region);
                      }}
                    >
                      {region.name}
                    </SuggestionItem>
                  ))}
                </SuggestionList>
              )}
              {touched.region && errors.region && <ErrorMessage>{errors.region}</ErrorMessage>}
            </InputWrapper>
          </InputGroup>
          <InputGroup>
            <InputWrapper hasError={touched.city && errors.city}>
              <Label>
                City <span>*</span>
              </Label>
              <Input
                type="text"
                placeholder={loadingCities ? 'Loading...' : 'Type to search...'}
                value={cityInput}
                onChange={(e) => handleCityChange(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, city: true }))}
                disabled={!selectedRegionId || loadingCities}
              />
              {citySuggestions.length > 0 && (
                <SuggestionList>
                  {citySuggestions.map((city) => (
                    <SuggestionItem
                      key={city.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectCity(city);
                      }}
                    >
                      {city.name}
                    </SuggestionItem>
                  ))}
                </SuggestionList>
              )}
              {touched.city && errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
            </InputWrapper>

            <InputWrapper>
              <Label>
                District <AptImg src={AptIcon} alt="info" />{' '}
              </Label>
              <Input
                type="text"
                placeholder={loadingDistricts ? 'Loading...' : 'Type to search...'}
                value={districtInput}
                onChange={(e) => handleDistrictChange(e.target.value)}
                disabled={!selectedCityId || loadingDistricts}
              />
              {districtSuggestions.length > 0 && (
                <SuggestionList>
                  {districtSuggestions.map((district) => (
                    <SuggestionItem
                      key={district.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectDistrict(district);
                      }}
                    >
                      {district.name}
                    </SuggestionItem>
                  ))}
                </SuggestionList>
              )}
            </InputWrapper>
          </InputGroup>
          <InputGroup>
            <InputWrapper flex={1} hasError={touched.street && errors.street}>
              <Label>
                Street <span>*</span>
              </Label>
              <Input
                type="text"
                placeholder={loadingStreets ? 'Loading...' : 'Type to search...'}
                value={streetInput}
                onChange={(e) => handleStreetChange(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, street: true }))}
                disabled={!selectedCityId || loadingStreets}
              />
              {streetSuggestions.length > 0 && (
                <SuggestionList>
                  {streetSuggestions.map((street) => (
                    <SuggestionItem
                      key={street.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectStreet(street);
                      }}
                    >
                      {street.name}
                    </SuggestionItem>
                  ))}
                </SuggestionList>
              )}
              {touched.street && errors.street && <ErrorMessage>{errors.street}</ErrorMessage>}
            </InputWrapper>

            <InputWrapper flex={1} hasError={touched.building && errors.building}>
              <Label>
                Building/House <span>*</span>
              </Label>
              <Input
                type="text"
                placeholder={loadingBuildings ? 'Loading...' : 'Type to search...'}
                value={buildingInput}
                onChange={(e) => handleBuildingChange(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, building: true }))}
                disabled={!selectedStreetId || loadingBuildings}
              />
              {buildingSuggestions.length > 0 && (
                <SuggestionList>
                  {buildingSuggestions.map((building) => (
                    <SuggestionItem
                      key={building.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectBuilding(building);
                      }}
                    >
                      {building.name}
                    </SuggestionItem>
                  ))}
                </SuggestionList>
              )}
              {touched.building && errors.building && (
                <ErrorMessage>{errors.building}</ErrorMessage>
              )}
            </InputWrapper>
          </InputGroup>
          <InputGroup>
            <InputWrapper flex={1}>
              <Label className="apt-label">
                Entrance <AptImg src={AptIcon} alt="info" />
              </Label>
              <Input
                type="text"
                placeholder="00"
                value={entrance}
                onChange={(e) => setEntrance(e.target.value)}
              />
            </InputWrapper>

            <InputWrapper flex={1}>
              <Label className="apt-label">
                Apt <AptImg src={AptIcon} alt="info" />
              </Label>
              <Input
                type="text"
                placeholder="12"
                value={apt}
                onChange={(e) => setApt(e.target.value)}
              />
            </InputWrapper>
          </InputGroup>
        </TabContent>
      </MainInfoContent>
    </Container>
  );
};

export default MainInfo;
